/**
 * Жесты двумя пальцами на холсте: щипок — зум, ведение — панорама.
 *
 * Один палец работает как мышь через pointer events и ничего здесь не трогает.
 * Как только на холсте два пальца, жест забирает события себе: холст двигается
 * и масштабируется вокруг точки между пальцами, а черчение и перетаскивание
 * должны быть отменены вызывающим (handlePointerDown вернёт true в этот момент).
 * Пока хоть один палец жеста не поднят, одиночные касания тоже глушатся —
 * иначе оставшийся палец начал бы стену или потащил объект.
 *
 * Хук ничего не знает о чертеже: ему нужны только zoomAt и panByPixels вьюпорта.
 */
import { useRef } from "react";
import type { PointerEvent } from "react";

interface Params {
	zoomAt: (clientX: number, clientY: number, factor: number) => void;
	panByPixels: (dxPx: number, dyPx: number) => void;
}

interface TouchPoint {
	x: number;
	y: number;
}

const midpoint = (a: TouchPoint, b: TouchPoint): TouchPoint => ({
	x: (a.x + b.x) / 2,
	y: (a.y + b.y) / 2,
});
const distance = (a: TouchPoint, b: TouchPoint) => Math.hypot(a.x - b.x, a.y - b.y);

export const useTouchGestures = ({ zoomAt, panByPixels }: Params) => {
	/** Пальцы на холсте сейчас */
	const touches = useRef(new Map<number, TouchPoint>());
	/** Жест двумя пальцами идёт или только что шёл — одиночные касания глушим */
	const gesture = useRef(false);
	const last = useRef<{ mid: TouchPoint; dist: number } | null>(null);

	const firstTwo = (): [TouchPoint, TouchPoint] | null => {
		const points = [...touches.current.values()];
		return points.length >= 2 ? [points[0], points[1]] : null;
	};

	/** @returns true — событие забрал жест, холсту его обрабатывать не нужно */
	const handlePointerDown = (e: PointerEvent<Element>): boolean => {
		if (e.pointerType !== "touch") return false;
		touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		const pair = firstTwo();
		if (pair) {
			gesture.current = true;
			last.current = { mid: midpoint(...pair), dist: distance(...pair) };
			return true;
		}
		return gesture.current;
	};

	/** @returns true — событие забрал жест */
	const handlePointerMove = (e: PointerEvent<Element>): boolean => {
		if (e.pointerType !== "touch" || !touches.current.has(e.pointerId)) return false;
		touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		const pair = firstTwo();
		if (!pair || !last.current) return gesture.current;
		const mid = midpoint(...pair);
		const dist = distance(...pair);
		panByPixels(mid.x - last.current.mid.x, mid.y - last.current.mid.y);
		if (last.current.dist > 0 && dist > 0) zoomAt(mid.x, mid.y, last.current.dist / dist);
		last.current = { mid, dist };
		return true;
	};

	const handlePointerUp = (e: PointerEvent<Element>) => {
		if (e.pointerType !== "touch") return;
		touches.current.delete(e.pointerId);
		last.current = null;
		// Все пальцы подняты — следующее касание снова обычный клик
		if (touches.current.size === 0) gesture.current = false;
	};

	return { handlePointerDown, handlePointerMove, handlePointerUp };
};
